import axios from "axios";
import { graphql, useStaticQuery } from "gatsby";
import React, { useEffect, useState } from "react";
import Recaptcha from "react-recaptcha";
import { useCookies } from "react-cookie";
import Select from "react-select";
import { Button } from "../Button/Button";
import Input from "../Input/Input";
import parse from "html-react-parser";
import Checkbox from "../Checkbox/Checkbox";
import { useForm, Controller } from "react-hook-form";
import envelope from "../../images/envelope.gif";

const styles = require("./HubspotFormV2.module.scss");
const textStyles = require("../../styles/richText.module.scss");

const defaultSelectStyles = {
  option: (provided) => ({
    ...provided,
    color: "black",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "white",
    opacity: 0.4,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "white",
  }),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "transparent",
    border: "1px solid white",
    height: "50px",
    width: "100%",
    color: "white",
    borderRadius: "0",
  }),
};

const validationRules = {
  required: "This field is required",
};

const HubspotForm = ({
  formId,
  selectStyles = defaultSelectStyles,
  title,
  titleDescription,
  recaptureId = "recapture",
  confirmationMessage = "",
  mandatoryConsent = false,
  consentError = "",
  alwaysConsent = false,
  submitButton = null,
  isPopUp = false,
}) => {
  const [legalConsentOptions, setLegalConsentOptions] = useState();
  const [checked, setChecked] = useState(false);
  const [hubspotForm, setHubspotForm] = useState();
  const [formError, setFormError] = useState();
  const [formSuccess, setFormSuccess] = useState();
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [cookies] = useCookies(["hubspotutk"]);

  const {
    handleSubmit: reactFormHandleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm();
  const handleCheckboxChange = (event) => {
    setChecked(event.target.checked);
  };

  const { allHubspotForm } = useStaticQuery(graphql`
    {
      allHubspotForm {
        nodes {
          id
          portalId
          name
          submitText
          formFieldGroups {
            fields {
              label
              name
              required
              fieldType
              hidden
              placeholder
              options {
                value
                label
              }
              unselectedLabel
              description
            }
          }
          inlineMessage
          method
          metaData {
            name
            value
          }
        }
      }
    }
  `);

  useEffect(() => {
    const newsletterForm = allHubspotForm?.nodes.find(
      (form) => form.id === formId
    );

    if (newsletterForm) {
      setHubspotForm(newsletterForm);

      const _legalConsentOptions = newsletterForm.metaData.find(
        (datum) => datum.name === "legalConsentOptions"
      );
      if (_legalConsentOptions.name && _legalConsentOptions.value) {
        setLegalConsentOptions(JSON.parse(_legalConsentOptions.value));
      }
    }
  }, [allHubspotForm]);

  const handleSubmit = async (data) => {
    const fields = [];
    let legalConsentAccepted = false;

    setFormError(false);

    if (!legalConsentOptions.communicationConsentCheckboxes.length) return;

    const communicationConsent =
      legalConsentOptions.communicationConsentCheckboxes[0];

    for (let el in data) {
      if (el === "legalConsentOptions") {
        legalConsentAccepted = data[el];
      } else if (el) {
        fields.push({
          name: el,
          value: typeof data[el] === "object" ? data[el].value : data[el],
        });
      }
    }

    try {
      const response = await axios.post(
        `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotForm.portalId}/${formId}`,
        {
          fields,
          legalConsentOptions: {
            consent: {
              consentToProcess: alwaysConsent
                ? alwaysConsent
                : legalConsentAccepted,
              text: `${legalConsentOptions.communicationConsentText} ${legalConsentOptions.privacyPolicyText}`,
              communications: [
                {
                  value: legalConsentAccepted,
                  subscriptionTypeId: communicationConsent.communicationTypeId,
                  text: communicationConsent.label,
                },
              ],
            },
          },
          context: {
            hutk: cookies.hubspotutk,
          },
        },
        {
          headers: {
            "content-type": `application/json`,
          },
        }
      );

      if (response.status === 200) {
        setFormSuccess(
          response.data?.inlineMessage || hubspotForm.inlineMessage
        );
      } else {
        setFormError(true);
      }
    } catch (error) {
      console.error(error);
      setFormError(true);
    }
  };

  const recaptchaVerifyCallback = async (token) => {
    // Verify the recaptcha response
    const { data: verificationResponse } = await axios.post(
      `${process.env.GATSBY_AMXCONNECT_API_URL}/api/verify-recaptcha`,
      { token }
    );

    if (verificationResponse.success === true) {
      setRecaptchaVerified(true);
    } else {
      setFormError(true);
    }
  };

  return (
    <React.Fragment>
      {formSuccess ? (
        <div className={styles.confirmationPage}>
          <img src={envelope} alt="envelope" className={styles.confirmIcon} />
          <div className={textStyles.content}>
            {parse(
              confirmationMessage === "" ? formSuccess : confirmationMessage
            )}
          </div>
        </div>
      ) : null}
      {!formSuccess && hubspotForm && legalConsentOptions ? (
        <React.Fragment>
          <h3>{title}</h3>
          <div className={styles.mainCopy}>{parse(titleDescription)}</div>
          <form>
            <fieldset className={styles.fieldsContainer}>
              {hubspotForm.formFieldGroups.map((formFieldGroup) => (
                <div className={styles.fieldGroup}>
                  {formFieldGroup.fields.map((field, index) => {
                    if (
                      field.fieldType === "text" ||
                      field.fieldType === "phonenumber"
                    ) {
                      return (
                        <Input
                          key={index}
                          {...register(field.name, validationRules)}
                          className={`${styles.textInput} ${
                            errors[field.name] ? styles.errorStyles : ""
                          }`}
                          placeholder={field.placeholder || field.label}
                        />
                      );
                    } else if (field.fieldType === "date") {
                      return (
                        <Input
                          key={index}
                          type="date"
                          {...register(field.name, validationRules)}
                          className={
                            errors[field.name] ? styles.errorStyles : ""
                          }
                          placeholder={field.placeholder || field.label}
                        />
                      );
                    } else if (field.fieldType === "select") {
                      return (
                        <Controller
                          key={index}
                          name={field.name}
                          control={control}
                          rules={validationRules}
                          render={({ field: fields }) => (
                            <Select
                              {...fields}
                              options={field.options}
                              placeholder={field.placeholder || field.label}
                              styles={{
                                ...selectStyles,
                                control: (provided, state) => ({
                                  ...provided,
                                  ...selectStyles.control(),
                                  border: errors[field.name]
                                    ? "1px solid #A50F52"
                                    : selectStyles.control().border,
                                }),
                              }}
                              components={{ IndicatorSeparator: () => null }}
                            />
                          )}
                        />
                      );
                    } else if (field.fieldType === "textarea") {
                      return (
                        <Input
                          key={index}
                          {...register(field.name, validationRules)}
                          className={
                            errors[field.name] ? styles.errorStyles : ""
                          }
                          type="textarea"
                          placeholder={field.placeholder || field.label}
                        />
                      );
                    }
                  })}
                </div>
              ))}
            </fieldset>

            <div className={styles.consentContainer}>
              <div className="ui">
                {parse(legalConsentOptions.communicationConsentText)}
              </div>
              {legalConsentOptions.communicationConsentCheckboxes?.length ? (
                <label className={styles.consentField}>
                  {mandatoryConsent ? (
                    <>
                      <Checkbox
                        {...register("legalConsentOptions", {
                          required: true,

                          message: { consentError },
                        })}
                        checked={checked}
                        className={`${styles.consentCheckBox} ${
                          errors["legalConsentOptions"] ? "checkBoxError" : ""
                        }`}
                        onChange={handleCheckboxChange}
                      />
                    </>
                  ) : (
                    <Checkbox
                      {...register("legalConsentOptions")}
                      checked={checked}
                      onChange={handleCheckboxChange}
                      className={styles.consentCheckBox}
                    />
                  )}
                  {"   "}
                  <span className="ui">
                    {parse(
                      legalConsentOptions.communicationConsentCheckboxes[0]
                        .label
                    )}
                  </span>
                </label>
              ) : null}
            </div>
          </form>

          <div className={styles.recaptcha}>
            <Recaptcha
              sitekey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
              verifyCallback={recaptchaVerifyCallback}
              elementID={recaptureId}
            />
          </div>
          <div className={styles.errorBox}>
            {formError ? (
              <p>There was an error submitting the form, please try again.</p>
            ) : null}
            {Object.keys(errors).length > 0 ? (
              <p className={styles.errorMsg}>
                fill in all required fields to submit
              </p>
            ) : null}
            {errors.consent && errors.consent.type === "required" && (
              <p className={styles.errorMsg}>
                You have to agree to receive the News and Insights from AMX to
                Submit
              </p>
            )}
          </div>
          <Button
            label={hubspotForm.submitText}
            bkgColor={submitButton?.bkgColor}
            className={styles.submitButton}
            disabled={!recaptchaVerified}
            handleOnClick={reactFormHandleSubmit(handleSubmit)}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default HubspotForm;
